import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { X, Library, Hash } from 'lucide-react';

/**
 * Compact series + tags editor that sits in the Sermon Prep left column
 * under the topic card. Series id is a free-text label; series_position
 * is an optional integer (e.g. "Week 3 of Romans"). Tags are space- or
 * comma-separated chips, normalised lowercase, capped at 20.
 */
const SermonSeriesPicker = ({
  language,
  seriesId,
  setSeriesId,
  seriesPosition,
  setSeriesPosition,
  tags = [],
  setTags,
}) => {
  const { t } = useLanguage();
  const [tagDraft, setTagDraft] = useState('');

  const addTag = (raw) => {
    const cleaned = (raw || '').trim().toLowerCase().slice(0, 30);
    if (!cleaned) return;
    setTags((prev) => {
      if (prev.includes(cleaned)) return prev;
      if (prev.length >= 20) return prev;
      return [...prev, cleaned];
    });
  };

  const removeTag = (tag) => setTags((prev) => prev.filter((x) => x !== tag));

  const onTagKeyDown = (e) => {
    // Enter or comma split into multiple tags. SPACE is allowed inside a tag
    // ("good friday") so multi-word topical tags work.
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const parts = tagDraft.split(',').map((s) => s.trim()).filter(Boolean);
      parts.forEach(addTag);
      setTagDraft('');
    } else if (e.key === 'Backspace' && tagDraft === '' && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <div
      className="rounded-md border border-border bg-card p-3 space-y-3"
      data-testid="sermon-series-picker"
    >
      <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        <Library className="w-3.5 h-3.5" />
        {t('sermon.seriesAndTags')}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Input
          className="sm:col-span-2"
          value={seriesId || ''}
          onChange={(e) => setSeriesId(e.target.value)}
          placeholder={t('sermon.seriesNamePlaceholder')}
          data-testid="sermon-series-id-input"
          maxLength={60}
        />
        <Input
          type="number"
          min={1}
          max={99}
          value={seriesPosition ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            setSeriesPosition(v === '' ? null : Math.max(1, Math.min(99, Number(v))));
          }}
          placeholder={t('sermon.weekHash')}
          data-testid="sermon-series-position-input"
        />
      </div>

      <div>
        <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="sermon-tags-list">
          {tags.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="text-[11px] flex items-center gap-1 cursor-pointer hover:bg-destructive/20"
              onClick={() => removeTag(tag)}
              data-testid={`sermon-tag-${tag}`}
            >
              <Hash className="w-2.5 h-2.5" />
              {tag}
              <X className="w-2.5 h-2.5 opacity-60" />
            </Badge>
          ))}
        </div>
        <Input
          value={tagDraft}
          onChange={(e) => setTagDraft(e.target.value)}
          onKeyDown={onTagKeyDown}
          onBlur={() => {
            if (tagDraft.trim()) {
              tagDraft.split(',').map((s) => s.trim()).filter(Boolean).forEach(addTag);
              setTagDraft('');
            }
          }}
          placeholder={t('sermon.tagsPlaceholder')}
          data-testid="sermon-tag-input"
          className="text-sm"
        />
      </div>
    </div>
  );
};

export default SermonSeriesPicker;
